import _ from 'lodash';

/** Product default when neither the form nor white-label supplies an accent. */
export const AUTOMY_ACCENT = '#296DFF';

/**
 * Host class for form-scoped accent tokens. Form `mainColor` must never paint
 * the shell — only descendants of this node (plus portaled overlays that opt in
 * via `FORM_ACCENT_PORTAL_CLASS`).
 */
export const FORM_STYLE_SCOPE = 'automy-form-style-scope';

/** Class for Ant Design dropdowns/pickers that portal outside the form host. */
export const FORM_ACCENT_PORTAL_CLASS = 'automy-form-accent-portal';

const FORM_ACCENT_STYLE_ID = 'automy-form-accent-tokens';

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Skip identical DOM writes (avoids style recalc when React re-wires the same form). */
let lastPaintSignature = '';

export function hex2RGB(hex) {
  const rgbChar = ['r', 'g', 'b'];

  const normal = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (normal) {
    return normal.slice(1).reduce((a, e, i) => {
      a[rgbChar[i]] = parseInt(e, 16);
      return a;
    }, {});
  }

  const shorthand = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (shorthand) {
    return shorthand.slice(1).reduce((a, e, i) => {
      a[rgbChar[i]] = 0x11 * parseInt(e, 16);
      return a;
    }, {});
  }

  return { r: 0, g: 0, b: 0 };
}

const rgbaFromRgb = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`;

export function isHexColor(value) {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim());
}

/**
 * Accent precedence used across the runtime UI:
 *   1. form `mainColor` (style / publicStyle)
 *   2. process portal shell (when active)
 *   3. company white-label (when it is not the AUTOMY default snapshot)
 *   4. AUTOMY product default
 */
export function resolveAccentColor(formMainColor) {
  if (isHexColor(formMainColor)) return formMainColor.trim();

  try {
    const {
      getPortalShellBrand,
      getCurrentWhiteLabel,
    } = require('@/utils/infrastructure/whiteLabel');
    const portal = getPortalShellBrand();
    if (portal && isHexColor(portal.mainColor)) return portal.mainColor;

    const wl = getCurrentWhiteLabel();
    if (wl && !wl.isDefault && isHexColor(wl.mainColor)) return wl.mainColor;
  } catch (e) {}

  return AUTOMY_ACCENT;
}

/**
 * CSS custom properties every accent-aware component should read. Soft variants
 * replace LESS `fade(@brand, n%)` so hovers/fills follow the same cascade.
 */
export function buildAccentCssVariables(mainColor, contrast = '#fff') {
  const accent = isHexColor(mainColor) ? mainColor.trim() : AUTOMY_ACCENT;
  const rgb = hex2RGB(accent);
  return {
    '--ant-primary-color': accent,
    '--form-accent-color': accent,
    '--form-accent-contrast': contrast || '#fff',
    '--form-accent-soft': rgbaFromRgb(rgb, 0.16),
    '--form-accent-soft-03': rgbaFromRgb(rgb, 0.03),
    '--form-accent-soft-05': rgbaFromRgb(rgb, 0.05),
    '--form-accent-soft-08': rgbaFromRgb(rgb, 0.08),
    '--form-accent-soft-10': rgbaFromRgb(rgb, 0.1),
    '--form-accent-soft-12': rgbaFromRgb(rgb, 0.12),
    '--form-accent-soft-24': rgbaFromRgb(rgb, 0.24),
    '--form-accent-soft-40': rgbaFromRgb(rgb, 0.4),
    '--form-accent-soft-45': rgbaFromRgb(rgb, 0.45),
  };
}

export function accentCssVariablesToText(vars) {
  return Object.entries(vars)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n');
}

/** Paint accent tokens on a DOM node (`:root` for platform, form scope for forms). */
export function applyAccentCssVariables(target, mainColor, contrast = '#fff') {
  if (!target?.style) return;
  const vars = buildAccentCssVariables(mainColor, contrast);
  Object.entries(vars).forEach(([key, value]) => target.style.setProperty(key, value));
  return vars;
}

export function getColorPalette(hex) {
  try {
    const { r, g, b } = hex2RGB(hex);

    const red = parseInt(r, 10) * 299;
    const green = parseInt(g, 10) * 587;
    const blue = parseInt(b, 10) * 114;

    const color = Math.round((red + green + blue) / 1000);

    const textColor = color > 125 ? 'black' : 'white';
    const backColor = `rgb(${r},${g},${b})`;

    return { textColor, backColor };
  } catch (e) {
    return {};
  }
}

export const getLightBackground = (hex, lightness = 0.8) => {
  if (!hex) return { background: 'transparent' };

  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 255;
  const g = (rgb >> 8) & 255;
  const b = rgb & 255;

  const lighten = (c) => Math.round(c + (255 - c) * lightness);
  const toHex = (rVal, gVal, bVal) =>
    `#${[rVal, gVal, bVal].map((x) => x.toString(16).padStart(2, '0')).join('')}`;

  return {
    color: hex,
    background: toHex(lighten(r), lighten(g), lighten(b)),
  };
};

function ensureFormAccentStyleEl() {
  let style = document.getElementById(FORM_ACCENT_STYLE_ID);
  if (!style) {
    // Element was removed by cleanup — force the next paint to write again.
    lastPaintSignature = '';
    style = document.createElement('style');
    style.id = FORM_ACCENT_STYLE_ID;
    // Keep `.custom-styles` so FormFieldGroups / External cleanup still works.
    style.setAttribute('class', 'custom-styles');
    style.setAttribute('type', 'text/css');
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(style);
  }
  return style;
}

/** Drop form tokens and reset the paint cache (call on form unmount). */
export function clearFormTokens() {
  lastPaintSignature = '';
  document.getElementById(FORM_ACCENT_STYLE_ID)?.remove();
}

/**
 * Upserts form tokens only (color + optional font). No component selectors —
 * chrome lives in `src/styles/form-accent-scope.less`.
 * Skips the DOM write when the signature is unchanged.
 */
export function paintFormTokens({ mainColor, textColor, font } = {}, scope = null) {
  const host = scope || `.${FORM_STYLE_SCOPE}`;
  const accent = isHexColor(mainColor) ? mainColor.trim() : '';
  const contrast = textColor || '#fff';
  const fontToken = font || '';
  const signature = `${host}|${accent}|${contrast}|${fontToken}`;

  if (signature === lastPaintSignature && document.getElementById(FORM_ACCENT_STYLE_ID)) {
    return;
  }

  const style = ensureFormAccentStyleEl();
  const decls = [];

  if (accent) {
    decls.push(accentCssVariablesToText(buildAccentCssVariables(accent, contrast)));
  }
  if (fontToken) {
    decls.push(`--form-font-family: ${fontToken}, sans-serif;`);
  }

  if (!decls.length) {
    style.textContent = '';
    lastPaintSignature = signature;
    return;
  }

  style.textContent = `${host}, .${FORM_ACCENT_PORTAL_CLASS} { ${decls.join('\n')} }`;
  lastPaintSignature = signature;
}

/** Loads a form font file; token application happens via paintFormTokens. */
export function setupDocumentFont({ fontUrl }) {
  if (!fontUrl) return;

  const alreadyLoaded = Array.from(document.querySelectorAll('link.custom-fonts')).some(
    (link) => link.getAttribute('data-form-font') === fontUrl,
  );
  if (alreadyLoaded) return;

  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('href', fontUrl);
  link.setAttribute('class', 'custom-fonts');
  link.setAttribute('data-form-font', fontUrl);
  document.head.appendChild(link);
}

export const getFontFamilyFromUrl = (fontUrl) => {
  const fontUrlTokens = fontUrl?.replace(/([ :.,;=/?@&]+)/g, '§sep§').split('§sep§');
  const familyPosition = fontUrlTokens?.findIndex((e) => e.toLowerCase() === 'family');
  return familyPosition > -1 ? fontUrlTokens?.[familyPosition + 1] : '';
};

/**
 * Pure: builds Header / tpl props from a form config. No DOM writes.
 * Use in render/memo; call `wireUpInlineStyles` or `paintFormTokens` in effects.
 */
export function buildFormStyleProps(form) {
  const attrs = _.pick({ ...form?.config, ...form?.style, ...form?.publicStyle }, [
    'description',
    'fontUrl',
    'header',
    'logoUrl',
    'mainColor',
    'template',
    'title',
    'name',
  ]);

  if (attrs?.mainColor) {
    Object.assign(attrs, getColorPalette(attrs.mainColor));
  }

  const accentColor = resolveAccentColor(attrs?.mainColor);
  const font = attrs?.fontUrl ? getFontFamilyFromUrl(attrs.fontUrl) : undefined;

  const nextState = {
    tplProps: {
      ...attrs,
      mainColor: attrs?.mainColor || undefined,
      accentColor,
      title: attrs?.title || attrs?.name || form?.name,
      logoUrl: attrs?.logoUrl || attrs?.header?.image?.downloadUrl,
      description: attrs?.description || attrs?.header?.text,
    },
  };

  if (font) Object.assign(nextState, { fontFamily: font });
  return nextState;
}

/** Builds form style props and paints tokens/fonts to the DOM. */
export function wireUpInlineStyles(form, scope = null) {
  const nextState = buildFormStyleProps(form);
  const { tplProps, fontFamily } = nextState;

  paintFormTokens(
    {
      mainColor: tplProps?.mainColor,
      textColor: tplProps?.textColor,
      font: fontFamily,
    },
    scope,
  );

  if (tplProps?.fontUrl) {
    setupDocumentFont({ fontUrl: tplProps.fontUrl });
  }

  return nextState;
}

/** @deprecated Prefer paintFormTokens. */
export function setupInlineStyle(attrs, scope = null) {
  paintFormTokens(
    {
      mainColor: attrs?.mainColor,
      textColor: attrs?.textColor,
    },
    scope,
  );
}
